import React, { useMemo, useState, useEffect } from "react";
import { Pie } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import styled from "styled-components";
import { Info } from "lucide-react";
import { Tooltip as InfoTooltip } from "react-tooltip";
import ChartDataLabels from "chartjs-plugin-datalabels";

// Register base plugins globally (NOT datalabels)
ChartJS.register(ArcElement, Tooltip, Legend);

// === Styled Components ===
const Wrapper = styled.div`
  width: 100%;
  padding: 100px;
`;

const ChartTitle = styled.div`
  font-family: Arial, sans-serif;
  font-size: 12px;
  font-weight: bold;
  color: #666;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const InfoIconWrapper = styled.div`
  cursor: pointer;
  color: #666;
  transition: color 0.3s;

  &:hover {
    color: #000;
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const Controls = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 20px;
  margin-bottom: 20px;
`;

const FilterGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Select = styled.select`
  padding: 8px 12px;
  font-size: 15px;
  border: 1px solid #ccc;
  border-radius: 4px;
`;

// === Utility ===
const EU_CODES = new Set([
  "AT","BE","BG","HR","CY","CZ","DK","EE","FI","FR","DE","GR","HU","IE","IT",
  "LV","LT","LU","MT","NL","PL","PT","RO","SK","SI","ES","SE"
]);

const parseValue = (value) => {
  const num = parseFloat(value);
  return isNaN(num) ? 0 : num;
};

// Colors: current = purple, red, light purple; previous = same hues, lighter alpha
const CURRENT_COLORS = ["#9848c7", "#e53935", "#d1b3ff"];   // KRAJ, UE, IMPORT
const PREVIOUS_COLORS = ["#9848c766", "#e5393566", "#d1b3ff66"]; // ~40% opacity

// === Component ===
const ZakupyTypeBreakdown = ({ data = [] }) => {
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState("all");

  const years = useMemo(
    () => [...new Set(data.map(d => parseInt(d.Rok)))].sort((a, b) => b - a),
    [data]
  );

  const availableMonths = useMemo(() => {
    if (!selectedYear) return [];
    return [...new Set(
      data
        .filter(d => parseInt(d.Rok) === parseInt(selectedYear))
        .map(d => parseInt(d.Miesiac))
    )].sort((a, b) => b - a);
  }, [data, selectedYear]);

  useEffect(() => {
    if (years.length > 0 && selectedYear == null) {
      setSelectedYear(years[0]);
    }
  }, [years, selectedYear]);

  const months = [
    ["all", "Cały rok"],
    ["1", "Styczeń"], ["2", "Luty"], ["3", "Marzec"], ["4", "Kwiecień"],
    ["5", "Maj"], ["6", "Czerwiec"], ["7", "Lipiec"], ["8", "Sierpień"],
    ["9", "Wrzesień"], ["10", "Październik"], ["11", "Listopad"], ["12", "Grudzień"]
  ];

  const labels = ["KRAJ", "UE", "IMPORT"];

  const getTotals = (year, month) => {
    const totals = { KRAJ: 0, UE: 0, IMPORT: 0 };

    const filtered = data.filter(d =>
      (!year || parseInt(d.Rok) === parseInt(year)) &&
      (month === "all" || parseInt(d.Miesiac) === parseInt(month))
    );

    filtered.forEach((row) => {
      const amount = parseValue(row.K_42) + parseValue(row.K_43);
      const kraj = (row.KodKrajuNadaniaTIN || "").trim().toUpperCase();

      if (kraj === "PL") {
        totals.KRAJ += amount;
      } else if (EU_CODES.has(kraj)) {
        totals.UE += amount;
      } else if (kraj) {
        totals.IMPORT += amount; // poza UE
      }
      // brak kraju -> pomijamy
    });

    return totals;
  };

  const {
    chartData,
    totalCurrent,
    totalPrev,
    prevYear
  } = useMemo(() => {
    const prevYear = selectedYear ? Number(selectedYear) - 1 : null;

    const cur = getTotals(selectedYear, selectedMonth);
    const prv = prevYear ? getTotals(prevYear, selectedMonth) : { KRAJ: 0, UE: 0, IMPORT: 0 };

    const currentValues = labels.map(l => cur[l]);
    const prevValues = labels.map(l => prv[l]);

    const totalCurrent = currentValues.reduce((a, b) => a + b, 0);
    const totalPrev = prevValues.reduce((a, b) => a + b, 0);

    return {
      prevYear,
      totalCurrent,
      totalPrev,
      chartData: {
        labels,
        datasets: [
          {
            label: selectedYear ? `Rok ${selectedYear}` : "Bieżący rok",
            data: currentValues,
            backgroundColor: CURRENT_COLORS,
            borderWidth: 1
          },
          {
            label: prevYear ? `Rok ${prevYear}` : "Poprzedni rok",
            data: prevValues,
            backgroundColor: PREVIOUS_COLORS,
            borderWidth: 1
          }
        ]
      }
    };
  }, [selectedYear, selectedMonth, data]);

  const hasAnyData = (totalCurrent > 0) || (totalPrev > 0);

  return (
    <Wrapper>
      <ChartTitle>
        Podział zakupów wg pochodzenia transakcji
        <InfoIconWrapper data-tooltip-id="zakupy-type-tooltip">
          <Info />
        </InfoIconWrapper>
      </ChartTitle>

      <InfoTooltip
        id="zakupy-type-tooltip"
        place="right"
        style={{ backgroundColor: "#333", color: "#fff", fontSize: "12px" }}
        content={
          <>
            <strong>Podział zakupów</strong><br />
            Udziały wartości dla kategorii: <em>KRAJ</em>, <em>UE</em>, <em>IMPORT</em>.<br />
            Dwa pierścienie porównują {selectedMonth === "all" ? "cały rok" : "wybrany miesiąc"} {selectedYear} vs { (selectedYear ? Number(selectedYear) - 1 : "poprzedni rok") }.
          </>
        }
      />

      <Controls>
        <FilterGroup>
          <label>Rok:</label>
          <Select value={selectedYear || ""} onChange={(e) => setSelectedYear(e.target.value)}>
            {years.map((y) => <option key={y} value={y}>{y}</option>)}
          </Select>
        </FilterGroup>

        <FilterGroup>
          <label>Miesiąc:</label>
          <Select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
          >
            {months.map(([val, label]) => {
              if (val !== "all" && selectedYear && availableMonths.length > 0 && !availableMonths.includes(Number(val))) {
                return null;
              }
              return <option key={val} value={val}>{label}</option>;
            })}
          </Select>
        </FilterGroup>
      </Controls>

      {hasAnyData ? (
        <div style={{ width: "85%", maxWidth: "650px", margin: "0 auto", height: "420px" }}>
          <Pie
            data={chartData}
            plugins={[ChartDataLabels]} // enable datalabels only here
            options={{
              responsive: true,
              maintainAspectRatio: false,
              plugins: {
                legend: { position: "bottom" },
                tooltip: {
                  callbacks: {
                    label: (ctx) => {
                      const ds = ctx.dataset;
                      const total = ds.data.reduce((a, b) => a + b, 0);
                      const val = ctx.raw;
                      const pct = total ? ((val / total) * 100).toFixed(1) : 0;
                      return `${ds.label} – ${ctx.label}: ${val.toLocaleString("pl-PL")} zł (${pct}%)`;
                    }
                  }
                },
                datalabels: {
                  color: "#fff",
                  font: { size: 12, weight: "bold" },
                  formatter: (value, ctx) => {
                    const total = ctx.dataset.data.reduce((a, b) => a + b, 0);
                    return total ? `${((value / total) * 100).toFixed(1)}%` : "";
                  }
                }
              }
            }}
          />
          <div style={{ textAlign: "center", marginTop: 10, color: "#666", fontSize: 12 }}>
            {selectedYear && (
              <>
                <span style={{ fontWeight: 600 }}>Zestawienie:</span>{" "}
                {selectedMonth === "all" ? "Cały rok" : `Miesiąc ${selectedMonth}`} {selectedYear} vs {prevYear}
              </>
            )}
          </div>
        </div>
      ) : (
        <p style={{ textAlign: "center", marginTop: "20px", color: "#888" }}>
          Brak danych do wyświetlenia.
        </p>
      )}
    </Wrapper>
  );
};

export default ZakupyTypeBreakdown;
